import { useState, useEffect, useRef } from 'react';
import { ListItem, Grid, Typography, Paper, IconButton, useTheme } from '@mui/material';
import { WorkOutlineRounded, EditRounded, WhatshotRounded } from '@mui/icons-material';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { Skeleton } from '@mui/material';
import { ColorPicker } from '../../../../../Global/Components';
import { grey, blue, red, green, amber, deepOrange, deepPurple } from '@mui/material/colors';
import { StatusButton } from '../../../Components';
import { range } from 'lodash';
import moment from 'moment';
import TaskBoxOptions from '../TaskBoxOptions';
import StateManager from '../../../../../Global/StateManager';
import TasksDialog from '../TasksDialog';
import useElementSize from '../../../../../Global/hooks/useElementSize';
import TaskManager from '../../../../../Global/TaskManager';
import { v4 } from 'uuid';
import axios from 'axios';
import MenuList from '../MenuList';

export default function TaskBox({ editable, onDelete, onEdit, params, onChange }) {
  const [dialog, setDialog] = useState(false);
  const [boxParams, setBoxParams] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [colorPicker, setColorPicker] = useState(null);
  const [color, setColor] = useState(undefined);
  const tasksRef = useRef([]);
  const deleteSubscription = useRef(null);
  const updateSubscription = useRef(null);
  const [elementId] = useState(v4());
  const { height, width } = useElementSize(elementId);
  const fontSize = Math.min(height / 2.5, width / 6);
  const theme = useTheme();

  useEffect(() => {
    if (!params || !params.type) return;
    setBoxParams(params);
    setColor(params.color || undefined);
    if (editable) return;
    setLoading(true);
    axios
      .post('/tasks/getTasksForBox', { box: params })
      .then((res) => {
        setTasks(res.data);
        tasksRef.current = res.data;

        const deleteCallback = ({ taskIds }) => {
          tasksRef.current = tasksRef.current.filter((x) => !taskIds.includes(x._id));
          setTasks(tasksRef.current);
        };

        const updateCallback = ({ task }) => {
          const index = tasksRef.current.findIndex((x) => x._id === task._id);
          if (index === -1) return;
          tasksRef.current[index] = { ...tasksRef.current[index], ...task };
          setTasks([...tasksRef.current]);
        };

        deleteSubscription.current = TaskManager.subscribeToAnyTaskDelete(deleteCallback);
        updateSubscription.current = TaskManager.subscribeToAnyTaskUpdate(updateCallback);

        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
      });
  }, [params, editable, params?.type, params?.id, params?.dueFilter]);

  const priorityColors = {
    None: grey[400],
    Low: green[500],
    Medium: amber[500],
    High: deepOrange[500],
    Critical: deepPurple[500],
  };

  const dueOptions = {
    today: { text: 'Due today', color: amber[600] },
    thisWeek: { text: 'Due this week', color: blue[600] },
    overdue: { text: 'Overdue', color: red[600] },
  };

  function getTitle() {
    const suffix = boxParams && dueOptions[boxParams?.dueFilter] ? `(${dueOptions[boxParams?.dueFilter].text})` : '';
    if (!boxParams?.type) return;
    if (boxParams?.type === 'my') return `My tasks  ${suffix}`;
    if (boxParams?.type === 'assigned') return `Assigned tasks ${suffix}`;
    if (boxParams?.type === 'projects') return `Tasks from all projects ${suffix}`;
    if (boxParams?.type === 'project') return `Tasks from project '${boxParams.title}' ${suffix}`;
    if (boxParams?.type === 'process') return `Tasks from process '${boxParams.title}' ${suffix}`;
    if (boxParams?.type === 'team') return `Tasks from team '${boxParams.title}' ${suffix}`;
    return '';
  }
  function saveColor(col) {
    setColor(col);
    onChange({ color: col });
  }

  function formatDate(date) {
    if (!date) return '-';
    const dt = moment(date);
    if (dt.isSame(moment(), 'day')) {
      // if today, only time
      return dt.format('hh:mm A');
    }
    return dt.format('DD/MM/YYYY');
  }

  function openTaskDialog() {
    if (tasks.length === 0) return;
    if (tasks.length === 1) {
      StateManager.selectTask(tasks[0]._id);
      return;
    }
    setDialogOpen(true);
  }

  const columns = [
    {
      field: 'priority',
      headerName: 'Priority',
      renderCell: (value, row) => (
        <Grid container item alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
          <WhatshotRounded
            style={{
              color: priorityColors[value.row.priority],
              display: 'block',
              margin: 'auto',
            }}
          />
        </Grid>
      ),
    },
    { field: 'title', headerName: 'Task Title', flex: 2 },
    {
      field: 'dueString',
      headerName: 'Due date',
      flex: 0.5,
      renderCell: (value, row) => (
        <Grid container item alignItems={'center'} justifyContent={'center'} sx={{ height: '100%' }}>
          <Typography
            style={{
              color: value.row.isOverdue ? red[500] : '',
              fontWeight: value.row.isOverdue ? 700 : 400,
            }}
            noWrap
          >
            {formatDate(value.row.dueAt)}
          </Typography>
        </Grid>
      ),
    },
    {
      field: 'progress',
      headerName: 'Status',
      flex: 0.6,
      renderCell: (value, row) => (
        <Grid container item alignItems={'center'} justifyContent={'center'} sx={{ height: '100%', borderRadius: 10 }}>
          <StatusButton
            status={value.row.progress}
            isDisabled
            isOverdue={value.row.isOverdue}
            style={{ maxHeigh: '100%', maxWidth: '100%' }}
          />
        </Grid>
      ),
    },
  ];

  if (!params || !params.type) return;
  return (
    <Grid container style={{ height: '100%', backgroundColor: color }} direction="column" id={elementId}>
      {editable ? (
        <>
          <ColorPicker
            anchor={colorPicker}
            open={Boolean(colorPicker)}
            column={400}
            customPicker
            onClose={(res) => {
              if (res?.color) {
                saveColor(res?.color);
              }
              setColorPicker(null);
            }}
            className="non-draggable"
          />
          {boxParams?.displayAs === 'table' && (
            <Grid container item alignItems="center" wrap="nowrap">
              <WorkOutlineRounded style={{ color: grey[500], marginRight: 16 }} />
              <Typography noWrap>Task box</Typography>
              <IconButton style={{ marginLeft: 'auto' }} onClick={(e) => setDialog(true)}>
                <EditRounded style={{ color: grey[500] }} />
              </IconButton>
              <MenuList
                onDelete={onDelete}
                color={color}
                onChange={onChange}
                setColorPicker={setColorPicker}
                mode={'taskBox'}
              />
            </Grid>
          )}

          <Grid container item alignItems="center" justifyContent="center" style={{ flex: 1 }} wrap="nowrap">
            <Typography color="textSecondary">{getTitle()}</Typography>
            {boxParams?.displayAs === 'tile' && (
              <>
                <IconButton style={{ marginLeft: 'auto' }} onClick={(e) => setDialog(true)}>
                  <EditRounded style={{ color: grey[500] }} />
                </IconButton>
                <MenuList
                  onDelete={onDelete}
                  color={color}
                  onChange={onChange}
                  setColorPicker={setColorPicker}
                  mode={'taskBox'}
                />
              </>
            )}
          </Grid>

          {editable && (
            <TaskBoxOptions
              open={dialog}
              onClose={() => setDialog(false)}
              initial={boxParams}
              onResult={(res) => {
                setBoxParams(res);
                onEdit(res);
              }}
            />
          )}
        </>
      ) : (
        <>
          {boxParams?.displayAs === 'tile' ? (
            loading ? (
              <Grid container alignItems="center" wrap="nowrap" justifyContent="space-between">
                <Skeleton variant="text" style={{ flexGrow: 1, marginRight: 16 }} />
                <Skeleton variant="circular" width={40} height={40} />
              </Grid>
            ) : (
              <ListItem
                disabled={tasks?.length === 0}
                button
                onClick={openTaskDialog}
                style={{ height: '100%', width: '100%', borderRadius: 5 }}
              >
                <Grid container alignItems="center" wrap="nowrap" justifyContent="space-between">
                  <Typography
                    variant="h6"
                    fontSize={fontSize ?? 16}
                    color={theme.palette.getContrastText(color || grey[400])}
                  >
                    {getTitle()}
                  </Typography>
                  <Typography
                    style={{
                      fontSize: fontSize * 1.3,
                      fontWeight: 600,
                      marginLeft: 8,
                      color: theme.palette.getContrastText(color || theme.palette.getContrastText(grey[400])),
                    }}
                  >
                    {tasks.length}
                  </Typography>
                </Grid>
              </ListItem>
            )
          ) : (
            <Grid container direction="column" style={{ height: '100%', position: 'relative', padding: 8 }}>
              <Grid container item alignItems="center" style={{ padding: 8, position: 'absolute', right: 0, top: 0 }}>
                {loading && (
                  <>
                    <Skeleton variant="circular" width={40} height={40} />
                    <Skeleton variant="text" width={100} style={{ marginLeft: 16 }} />
                  </>
                )}
              </Grid>

              <Grid
                container
                item
                alignContent="flex-start"
                direction={'column'}
                style={{ padding: 8, flex: 1, overflow: 'auto' }}
              >
                {loading ? (
                  range(5).map((x) => (
                    <Grid
                      container
                      component={Paper}
                      key={x}
                      variant="outlined"
                      sx={{ p: 0, my: 1, border: `2px solid ${grey[300]}`, borderRadius: 1.5 }}
                    >
                      <ListItem sx={{ py: 1, px: 2, width: '100%', borderRadius: 1.5 }}>
                        <Grid container item alignItems="center">
                          <Skeleton variant="circular" width={40} height={40} />
                          <Skeleton variant="text" width={150} style={{ marginLeft: 16 }} />
                        </Grid>
                      </ListItem>
                    </Grid>
                  ))
                ) : (
                  <>
                    <Grid container item direction={'row'}>
                      <WorkOutlineRounded style={{ color: grey[500], marginRight: 16 }} />
                      <Typography variant="h6">{getTitle()}</Typography>
                    </Grid>

                    <DataGridPremium
                      columns={columns}
                      rows={tasks}
                      getRowId={(row) => row._id}
                      onRowClick={(task) => StateManager.selectTask(task.id)}
                      sx={{ width: '100%' }}
                    />
                  </>
                )}
              </Grid>
            </Grid>
          )}
          <TasksDialog tasks={tasks} title={getTitle()} open={dialogOpen} onClose={() => setDialogOpen(false)} />
        </>
      )}
    </Grid>
  );
}
